import { z } from "zod";
import { BACKGROUND_NAMES, BGM_NAMES, COMPANION_EXPRESSIONS } from "../assets/paths";

/**
 * 教材JSON（`public/data/`）のZodスキーマ。**ドメイン層はzodに依存できない**ため、
 * スキーマはインフラ層に置き、検証を通ったものだけを `mapper.ts` でドメインの
 * 値オブジェクトへ変換する（腐敗防止層。ADR-006）。
 *
 * すべてのオブジェクトスキーマは `.strict()` とし、未知のフィールドを拒否する
 * （タイポ等の混入をスキーマの段階で検出する。ADR-006の影響欄「Zodスキーマの品質が
 * 教材の品質を守る唯一の砦」）。
 */

const modeSchema = z.enum([
  "normal",
  "insert",
  "visual",
  "visual-line",
  "visual-block",
  "command-line",
]);

const cursorSchema = z
  .object({
    line: z.number().int().min(0),
    col: z.number().int().min(0),
  })
  .strict();

const keystrokeSchema = z
  .object({
    key: z.string().min(1),
    ctrl: z.boolean().optional(),
  })
  .strict();

/**
 * `buffer` と `cursor` の少なくとも一方が定義されていることをスキーマレベルで強制する
 * （何も判定しない演習を教材に混入させない。F-004）。
 */
const drillGoalSchema = z
  .object({
    buffer: z.array(z.string()).optional(),
    cursor: cursorSchema.optional(),
    requireMode: modeSchema.optional(),
    // その段で学んだ技を実際に使ったかを判定に含めるための指定（`judge()` 参照）。
    requiredCommands: z.array(z.string().min(1)).min(1).optional(),
  })
  .strict()
  .refine((goal) => goal.buffer !== undefined || goal.cursor !== undefined, {
    message: "DrillGoalはbufferとcursorの少なくとも一方が必須です",
  });

const hintSchema = z.string().min(1);

const drillSchema = z
  .object({
    id: z.string().min(1),
    instruction: z.string().min(1),
    initialBuffer: z.array(z.string()),
    initialCursor: cursorSchema,
    goal: drillGoalSchema,
    keystrokeLimit: z.number().int().positive(),
    hints: z.array(hintSchema),
    // 模範解答（任意）。判定には使わない（承認事項A）。
    solution: z.array(keystrokeSchema).optional(),
  })
  .strict();

const lessonSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    drills: z.array(drillSchema).min(1),
  })
  .strict();

const levelExamSchema = z
  .object({
    id: z.string().min(1),
    // 昇段試験はその段の技をすべて問う複合課題の連続（1問以上）。
    drills: z.array(drillSchema).min(1),
  })
  .strict();

const levelFileSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    summary: z.string().min(1),
    commands: z.array(z.string().min(1)).min(1),
    lessons: z.array(lessonSchema).min(1),
    exam: levelExamSchema,
  })
  .strict();

const manifestEntrySchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    summary: z.string().min(1),
    commands: z.array(z.string().min(1)).min(1),
    /**
     * レッスンに含まれる演習の総数（最終テストは含まない）。稽古の段（S-1）が
     * `level-N.json` 全体を読まずに「つづき n / m」を表示できるようにするため、
     * manifestの時点で持たせる（計画10着手時の決定）。
     */
    drillCount: z.number().int().nonnegative(),
  })
  .strict();

const manifestSchema = z
  .object({
    levels: z.array(manifestEntrySchema).min(1),
  })
  .strict();

const emphasisSchema = z
  .object({
    start: z.number().int().min(0),
    end: z.number().int().min(0),
  })
  .strict()
  .refine((emphasis) => emphasis.end > emphasis.start, {
    message: "emphasisはend > startである必要があります",
  });

const lineSchema = z
  .object({
    speaker: z.string().min(1),
    text: z.string().min(1),
    emphasis: z.array(emphasisSchema),
    expression: z.enum(COMPANION_EXPRESSIONS),
  })
  .strict();

const sceneSchema = z
  .object({
    backgroundPath: z.enum(BACKGROUND_NAMES),
    bgmPath: z.enum(BGM_NAMES),
    lines: z.array(lineSchema).min(1),
  })
  .strict();

const chapterSchema = z
  .object({
    id: z.string().min(1),
    levelId: z.string().min(1),
    kind: z.enum(["intro", "outro"]),
    scenes: z.array(sceneSchema).min(1),
  })
  .strict();

const companionReactionSchema = z
  .object({
    trigger: z.enum([
      "pass",
      "fail",
      "off-technique",
      "over-limit",
      "idle",
      "unlearned-key",
      "repeated-fail",
      "chat",
      "tip",
    ]),
    lines: z.array(lineSchema).min(1),
  })
  .strict();

const storyFileSchema = z
  .object({
    levelId: z.string().min(1),
    intro: chapterSchema,
    outro: chapterSchema,
    companionReactions: z.array(companionReactionSchema),
  })
  .strict();

export const ContentSchema = {
  manifest: manifestSchema,
  levelFile: levelFileSchema,
  storyFile: storyFileSchema,
};

export type ManifestData = z.infer<typeof manifestSchema>;
export type LevelFileData = z.infer<typeof levelFileSchema>;
export type StoryFileData = z.infer<typeof storyFileSchema>;
